import BaseApi from './BaseApi.js';

class OrganizationApi extends BaseApi {
	/**
	 * Get organization dashboard data including organization info, locations, agenda, and menu
	 * @returns {Promise<Object>} Organization dashboard data
	 */
	static async getOrganizationDashboard() {
		const orgUnique = this.getOrganizationUnique();
		const endpoint = `/org/${orgUnique}/dashboard`;
		return await this.get(endpoint);
	}

	/**
	 * Get organization by ID
	 * @param {string|number} organizationId - Organization ID
	 * @returns {Promise<Object>} Organization data
	 */
	static async getOrganizationById(organizationId) {
		const endpoint = `/organization/${organizationId}`;
		return await this.get(endpoint);
	}

	/**
	 * Get organization mechanics
	 * @returns {Promise<Array>} Organization mechanics
	 */
	static async getOrganizationMechanics() {
		const orgUnique = this.getOrganizationUnique();
		const endpoint = `/org/${orgUnique}/mechanics`;
		return await this.get(endpoint);
	}

	/**
	 * Get organization pilots
	 * @returns {Promise<Array>} Organization pilots
	 */
	static async getOrganizationPilots() {
		const orgUnique = this.getOrganizationUnique();
		const endpoint = `/org/${orgUnique}/pilots`;
		return await this.get(endpoint);
	}

	/**
	 * Get organization agenda/events
	 * @returns {Promise<Object>} Organization agenda
	 */
	static async getOrganizationAgenda() {
		const orgUnique = this.getOrganizationUnique();
		const endpoint = `/org/${orgUnique}/agenda`;
		return await this.get(endpoint);
	}

	/**
	 * Get organization locations
	 * @returns {Promise<Array>} Organization locations
	 */
	static async getOrganizationLocations() {
		const orgUnique = this.getOrganizationUnique();
		const endpoint = `/org/${orgUnique}/location`;
		return await this.get(endpoint);
	}

	/**
	 * Get the locations shown on the organization dashboard. Unlike `/location`, it is open to
	 * every dashboard viewer (Member, Inactive), and returns display fields only.
	 * @returns {Promise<Object>} { data: { data: Array<{ id, name, iata, airportName, address,
	 *   addressSecond, city, stateName, zip, countryName, services: string[] }> } }
	 */
	static async getOrganizationDashboardLocations() {
		const orgUnique = this.getOrganizationUnique();
		const endpoint = `/org/${orgUnique}/dashboard/locations`;
		return await this.get(endpoint);
	}

	/**
	 * POST /organizations/additional-data — bulk fetch per-org settings,
	 * customFields, and activityTypes for the supplied organization IDs.
	 * Backend is gated by `calendarView` permission, so the response is
	 * automatically restricted to orgs the calendar can see.
	 *
	 * @param {Array<number>} organizationIds — 1..50 positive integers
	 * @returns {Promise<Object>} `{ [orgId]: { id, settings, customFields, activityTypes } }`
	 */
	static async getOrganizationsAdditionalData(organizationIds) {
		return await this.post('/organizations/additional-data', { organizationIds });
	}
}

export default OrganizationApi;
